import React, { useState, useEffect } from 'react';
import apiClient, { permissionMessage } from '../config/axiosConfig';
import { useAuth } from '../hooks/useAuth';

interface Location {
  id: number;
  name: string;
  city: string;
  address_line1?: string;
  postal_code?: string;
}

interface LocationSelectorProps {
  onLocationSelected: (location: Location) => void;
  onAddLocation: () => void;
  onManageUsers: () => void;
  locations: Location[];
  isLoading: boolean;
  onRefreshLocations: () => void;
  /** Alleen de superadmin mag locaties toevoegen of verwijderen. */
  canManageLocations: boolean;
}

const LocationSelector: React.FC<LocationSelectorProps> = ({ 
  onLocationSelected, 
  onAddLocation,
  onManageUsers,
  locations,
  isLoading,
  onRefreshLocations,
  canManageLocations
}) => {
  const [error, setError] = useState<string | null>(null);
  const [deleteMode, setDeleteMode] = useState(false);
  const { logout } = useAuth();

  const handleLocationSelect = (location: Location) => {
    onLocationSelected(location);
  };

  const handleLogout = () => {
    logout();
  };

  const deleteLocation = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click event
    if (window.confirm('Weet je zeker dat je deze locatie wilt verwijderen? Dit kan alleen als er geen fietsen aan deze locatie gekoppeld zijn.')) {
      try {
        await apiClient.delete(`/locations/${id}`);
        onRefreshLocations(); // Refresh locations list from parent
        setDeleteMode(false); // Exit delete mode after deletion
      } catch (error: any) {
        console.error('Error deleting location:', error);
        const forbidden = permissionMessage(error);
        if (forbidden) {
          setError(forbidden);
        } else if (error.response?.status === 401) {
          logout();
        } else if (error.response?.status === 400) {
          alert('Deze locatie kan niet worden verwijderd omdat er nog fietsen aan gekoppeld zijn.');
        } else {
          alert('Er is een fout opgetreden bij het verwijderen van de locatie.');
        }
      }
    }
  };

  if (isLoading) {
    return (
      <div className="location-selector">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Locaties laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="location-selector">
      <div className="location-selector-container">
        <button
            className="logout-btn align right"
            onClick={handleLogout}
        >
            Uitloggen
        </button>
        <div className="location-selector-header">
          <h1>Selecteer Locatie</h1>
          <p>Kies de locatie die je wilt beheren</p>
          {canManageLocations && (
            <div className="header-actions">
              <button
                className="btn btn-secondary"
                onClick={onManageUsers}
              >
                👥 Beheerders
              </button>
              <button 
                className={`btn ${deleteMode ? 'btn-secondary' : 'btn-danger'}`}
                onClick={() => setDeleteMode(!deleteMode)}
              >
                {deleteMode ? '✓ Annuleer Verwijderen' : '🗑️ Verwijder Modus'}
              </button>
              <button 
                className="add-location-btn"
                onClick={onAddLocation}
              >
                + Nieuwe Locatie Toevoegen
              </button>
            </div>
          )}
        </div>

        {error && (
          <div className="delete-mode-warning">
            <span className="warning-icon">⚠️</span>
            <span>{error}</span>
          </div>
        )}

        {deleteMode && canManageLocations && (
          <div className="delete-mode-warning">
            <span className="warning-icon">⚠️</span>
            <span>Verwijder modus actief - Klik op de verwijder knop bij een locatie om deze te verwijderen</span>
          </div>
        )}

        <div className="locations-grid">
        {locations.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📍</div>
            <h3>Geen locaties gevonden</h3>
            <p>
              {canManageLocations
                ? 'Er zijn nog geen locaties. Voeg er hierboven een toe.'
                : 'Je bent nog niet aan een locatie gekoppeld. Vraag de superadmin om je toe te voegen.'}
            </p>
          </div>
        ) : (
          locations.map((location) => (
            <div 
              key={location.id} 
              className="location-card"
              onClick={deleteMode && canManageLocations ? undefined : () => handleLocationSelect(location)}
              style={{ cursor: deleteMode && canManageLocations ? 'default' : 'pointer' }}
            >
              <div className="location-card-header">
                <div className="location-icon">🏢</div>
                <div className="location-name">{location.name}</div>
              </div>
              <div className="location-card-body">
                <div className="location-info">
                  <div className="info-item">
                    <span className="info-label">Stad:</span>
                    <span className="info-value">{location.city}</span>
                  </div>
                  {location.address_line1 && (
                    <div className="info-item">
                      <span className="info-label">Adres:</span>
                      <span className="info-value">{location.address_line1}</span>
                    </div>
                  )}
                  {location.postal_code && (
                    <div className="info-item">
                      <span className="info-label">Postcode:</span>
                      <span className="info-value">{location.postal_code}</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="location-card-footer">
                {deleteMode && canManageLocations ? (
                  <button 
                    className="action-btn delete-btn"
                    onClick={(e) => deleteLocation(location.id, e)}
                    style={{ 
                      padding: '12px 24px',
                      fontSize: '1rem',
                      fontWeight: '600',
                      borderRadius: '8px'
                    }}
                  >
                    🗑️ Verwijderen
                  </button>
                ) : (
                  <button className="select-location-btn">
                    Beheer deze locatie
                    <span className="arrow">→</span>
                  </button>
                )}
              </div>
            </div>
          ))
        )}
        </div>
      </div>
    </div>
  );
};

export default LocationSelector;
